import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen,
  ShieldAlert,
  ArrowLeft,
  Loader2,
  Mail,
  CheckCircle2,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Public form used at /unsubscribe. Deliberately does NOT go through the
// admin-scoped axios instance (which would attach X-Admin-Id and could clear
// admin auth on 4xx). We use a bare axios instance instead.
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

function messageFromError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(", ");
  if (typeof detail === "string") return detail;
  return err?.message || "Something went wrong. Please try again.";
}

function Header() {
  return (
    <header className="border-b border-border/60 bg-background/80 backdrop-blur sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="unsub-brand-link">
          <div className="size-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <BookOpen className="size-4 text-white" />
          </div>
          <span className="text-sm font-semibold tracking-tight">The Greater Life</span>
        </Link>
        <Link
          to="/privacy"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="unsub-privacy-link"
        >
          Privacy Policy
        </Link>
      </div>
    </header>
  );
}

function StepIndicator({ step }) {
  const steps = ["Request", "Verify email", "Received"];
  return (
    <ol className="flex items-center gap-2 text-xs mb-6" data-testid="unsub-step-indicator">
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={`size-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                done
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                  : active
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {done ? "✓" : idx}
            </span>
            <span className={active ? "text-foreground font-medium" : "text-muted-foreground"}>
              {label}
            </span>
            {idx < steps.length && <span className="w-6 h-px bg-border/70 mx-1" />}
          </li>
        );
      })}
    </ol>
  );
}

function RequestStep({ form, setForm, onSubmit, loading }) {
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="unsub-request-form">
      <div className="space-y-1.5">
        <Label htmlFor="unsub-email">Email address</Label>
        <Input
          id="unsub-email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          data-testid="unsub-email-input"
        />
        <p className="text-xs text-muted-foreground">
          Use the email address associated with your account.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="unsub-name">Full name (optional)</Label>
        <Input
          id="unsub-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="First and last name"
          data-testid="unsub-name-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="unsub-reason">Reason (optional)</Label>
        <Textarea
          id="unsub-reason"
          rows={3}
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Tell us why you'd like your data removed. This helps us improve."
          data-testid="unsub-reason-input"
        />
      </div>
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-500/40 bg-amber-50/70 dark:bg-amber-500/10 p-3">
        <ShieldAlert className="size-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
          Once approved, your account, comments, likes and any personal data will be permanently
          erased. Payment records may be kept in anonymised form for accounting compliance.
        </div>
      </div>
      <label
        className="flex items-start gap-3 cursor-pointer select-none"
        data-testid="unsub-consent-label"
      >
        <Checkbox
          checked={form.consent}
          onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
          className="mt-0.5"
          data-testid="unsub-consent-checkbox"
        />
        <span className="text-sm leading-snug">
          I understand that this action is <span className="font-semibold">permanent</span> and
          cannot be undone. I confirm the email above belongs to me.
        </span>
      </label>
      <Button
        type="submit"
        disabled={loading || !form.consent || !form.email}
        className="w-full"
        data-testid="unsub-submit-button"
      >
        {loading ? (
          <Loader2 className="size-4 mr-2 animate-spin" />
        ) : (
          <Mail className="size-4 mr-2" />
        )}
        Send verification code
      </Button>
    </form>
  );
}

function VerifyStep({ email, code, setCode, onVerify, onResend, loading, resending }) {
  return (
    <form onSubmit={onVerify} className="space-y-5" data-testid="unsub-verify-form">
      <div className="rounded-lg bg-muted/50 border border-border/70 p-3 text-sm">
        We've sent a 6-digit code to{" "}
        <span className="font-semibold" data-testid="unsub-verify-email">{email}</span>. It expires
        in 10 minutes.
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="unsub-otp">Verification code</Label>
        <Input
          id="unsub-otp"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="text-lg tracking-[0.4em] text-center font-mono"
          data-testid="unsub-otp-input"
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          type="submit"
          disabled={loading || code.length !== 6}
          className="flex-1"
          data-testid="unsub-verify-button"
        >
          {loading ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
          Confirm request
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={resending}
          onClick={onResend}
          data-testid="unsub-resend-button"
        >
          {resending ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
          Resend code
        </Button>
      </div>
    </form>
  );
}

function DoneStep({ email }) {
  return (
    <div className="space-y-5 text-center py-6" data-testid="unsub-done-panel">
      <div className="mx-auto size-14 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
        <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Your request has been received</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Thank you. Our team will review the request submitted from{" "}
          <span className="font-medium text-foreground">{email}</span> and process the erasure of
          your personal data in accordance with the Nigeria Data Protection Act (NDPA). You'll
          receive an email confirmation once your data has been deleted.
        </p>
      </div>
      <div className="flex items-start gap-3 rounded-lg bg-muted/40 border border-border/70 p-3 text-left max-w-md mx-auto">
        <Info className="size-4 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Requests are typically completed within 30 days. If you change your mind, contact us
          before your request is processed and we'll cancel it.
        </p>
      </div>
      <Link to="/" className="inline-block">
        <Button variant="outline" data-testid="unsub-home-button">
          <ArrowLeft className="size-4 mr-2" /> Back to home
        </Button>
      </Link>
    </div>
  );
}

export default function UnsubscribePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: "", name: "", reason: "", consent: false });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the confirmation checkbox to continue.");
      return;
    }
    setLoading(true);
    try {
      await publicApi.post("/data-deletion/request", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || null,
        reason: form.reason.trim() || null,
      });
      toast.success("Verification code sent");
      setStep(2);
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await publicApi.post("/data-deletion/verify", {
        email: form.email.trim().toLowerCase(),
        code,
      });
      setStep(3);
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await publicApi.post("/data-deletion/resend-otp", {
        email: form.email.trim().toLowerCase(),
      });
      toast.success("A new code has been sent");
      setCode("");
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 py-10 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          <div className="mb-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-3">
              <ShieldAlert className="size-3.5" /> NDPA · Right to Erasure
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Delete my personal data
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              Submit a request to have your account and personal data permanently removed from The
              Greater Life. We'll verify your email to confirm the request is yours.
            </p>
          </div>

          <div className="rounded-2xl border border-border/70 bg-card shadow-sm p-6 sm:p-8">
            <StepIndicator step={step} />

            {step === 1 && (
              <RequestStep form={form} setForm={setForm} onSubmit={submitRequest} loading={loading} />
            )}
            {step === 2 && (
              <VerifyStep
                email={form.email}
                code={code}
                setCode={setCode}
                onVerify={verify}
                onResend={resend}
                loading={loading}
                resending={resending}
              />
            )}
            {step === 3 && <DoneStep email={form.email} />}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Questions? Reach us via{" "}
            <a href="mailto:hello@thegreaterlife.org" className="underline">
              hello@thegreaterlife.org
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
